import { useState, useEffect } from "react";
import { useGeoPersonalization } from "./useGeoPersonalization";

interface FormPersonalization {
  intent: string;
  urgency: string;
  headline: string;
  subheadline: string;
  ctaButton: string;
  urgencyBanner: string | null;
  socialProof: string;
  formFields: string[];
  financing: { available: boolean; text: string } | null;
  isLoading: boolean;
}

export const useSmartFormPersonalization = (pagePath: string): FormPersonalization => {
  const geo = useGeoPersonalization();
  const [personalization, setPersonalization] = useState<FormPersonalization>({
    intent: "general",
    urgency: "medium",
    headline: "Expert Roofing in Southwest Florida",
    subheadline: "Licensed, insured, and certified. Free estimates.",
    ctaButton: "Get Free Estimate",
    urgencyBanner: null,
    socialProof: "Trusted by 2,500+ Southwest Florida homeowners",
    formFields: ["name", "phone", "email"],
    financing: { available: true, text: "$0 down, from $99/mo" },
    isLoading: true,
  });

  useEffect(() => {
    // Client-side personalization (no edge function call needed for basic detection)
    const month = new Date().getMonth() + 1;
    const isStormSeason = month >= 6 && month <= 11;
    const city = geo.city;

    let intent = "general";
    let urgency = "medium";
    let estimatedValue = 8000;

    if (pagePath?.includes("emergency") || pagePath?.includes("leak") || pagePath?.includes("storm")) {
      intent = "emergency";
      urgency = "critical";
      estimatedValue = 3000;
    } else if (pagePath?.includes("replacement") || pagePath?.includes("new-roof")) {
      intent = "replacement";
      urgency = isStormSeason ? "high" : "medium";
      estimatedValue = 15000;
    } else if (pagePath?.includes("repair")) {
      intent = "repair";
      urgency = "high";
      estimatedValue = 2500;
    } else if (pagePath?.includes("insurance") || pagePath?.includes("claim")) {
      intent = "insurance";
      urgency = "high";
      estimatedValue = 18000;
    } else if (pagePath?.includes("metal")) {
      intent = "metal-roofing";
      urgency = "medium";
      estimatedValue = 20000;
    } else if (pagePath?.includes("tile")) {
      intent = "tile-roofing";
      urgency = "medium";
      estimatedValue = 25000;
    } else if (pagePath?.includes("inspection") || pagePath?.includes("estimate")) {
      intent = "inspection";
      urgency = "low";
      estimatedValue = 0;
    }

    const copyMap: Record<string, { headline: string; subheadline: string; ctaButton: string; urgencyBanner: string | null }> = {
      emergency: {
        headline: "24/7 Emergency Roof Repair",
        subheadline: `${city ? `Serving ${city}` : "Southwest Florida"} — crews dispatched within 2 hours`,
        ctaButton: "Get Emergency Help Now",
        urgencyBanner: "⚡ Emergency teams available today — call or submit for priority dispatch",
      },
      replacement: {
        headline: `New Roof? Get Your Free Estimate${city ? ` in ${city}` : ""}`,
        subheadline: isStormSeason
          ? "Hurricane season is here — protect your home before the next storm"
          : "Lock in dry-season pricing — faster installs, no rain delays",
        ctaButton: "Get My Free Estimate",
        urgencyBanner: isStormSeason ? "🌀 Storm season pricing ends soon — book now" : "☀️ Dry season special — save $500 on your new roof",
      },
      repair: {
        headline: `Fast Roof Repair${city ? ` in ${city}` : ""}`,
        subheadline: "Same-day service available. Licensed & insured. Free damage assessment.",
        ctaButton: "Schedule Free Inspection",
        urgencyBanner: "🔧 Same-day repair appointments available this week",
      },
      insurance: {
        headline: "We Handle Your Roof Insurance Claim",
        subheadline: "Free inspection, full documentation, adjuster meeting support. $0 out of pocket.",
        ctaButton: "Start My Claim",
        urgencyBanner: "📋 Florida insurance deadlines apply — don't wait to file",
      },
      "metal-roofing": {
        headline: `Premium Metal Roofing${city ? ` in ${city}` : ""}`,
        subheadline: "50-year warranty. Hurricane-rated. Energy savings up to 25%.",
        ctaButton: "Get Metal Roof Quote",
        urgencyBanner: null,
      },
      "tile-roofing": {
        headline: `Tile Roof Experts${city ? ` in ${city}` : ""}`,
        subheadline: "Concrete & clay tile installation and repair. Florida Building Code certified.",
        ctaButton: "Get Tile Roof Quote",
        urgencyBanner: null,
      },
      inspection: {
        headline: "Free Roof Inspection",
        subheadline: "Comprehensive 21-point inspection. Report within 24 hours. No obligation.",
        ctaButton: "Book Free Inspection",
        urgencyBanner: "✅ Same-week inspection slots available",
      },
      general: {
        headline: `Expert Roofing${city ? ` in ${city}` : " in Southwest Florida"}`,
        subheadline: "Licensed, insured, and certified. Free estimates. Financing available.",
        ctaButton: "Get Free Estimate",
        urgencyBanner: isStormSeason ? "🌀 Storm season — is your roof ready?" : null,
      },
    };

    const copy = copyMap[intent] || copyMap.general;

    const formFields = ["name", "phone", "email"];
    if (intent === "emergency") formFields.push("urgency_description");
    else if (intent === "insurance") formFields.push("insurance_company");
    else formFields.push("address");

    const socialProof = city
      ? `Trusted by ${Math.floor(300 + city.length * 7)}+ ${city} homeowners`
      : "Trusted by 2,500+ Southwest Florida homeowners";

    setPersonalization({
      intent,
      urgency,
      ...copy,
      socialProof,
      formFields,
      financing: intent !== "inspection" ? { available: true, text: "$0 down, from $99/mo" } : null,
      isLoading: false,
    });
  }, [pagePath, geo.city]);

  return personalization;
};
